import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

/**
 * Custom hook to create and update a D3.js horizontal bar chart
 * @param {Array} data - Array of objects with country and value properties
 * @param {Object} options - Configuration options for the chart
 * @returns {Object} - Ref to attach to the SVG element
 */
const useD3BarChart = (data, options = {}) => {
  const svgRef = useRef(null);
  
  // Default options
  const {
    width = 800,
    height = 600,
    margin = { top: 30, right: 30, bottom: 40, left: 80 },
    barHeight = 40,
    barPadding = 10,
    highlightCountry = 'Brasil',
    duration = 750,
    formatSuffix = 'K',
    backgroundColor = '#4CAF50', // Green background color
    barColor = '#f5f5f5',        // Light color for regular bars
    highlightColor = '#333333',  // Dark color for highlighted bar
    textColor = '#333333',       // Text color
    valueTextColor = '#333333',  // Value text color
  } = options;

  useEffect(() => {
    if (!data || !svgRef.current) return;

    // Sort data by value in descending order
    const sortedData = [...data].sort((a, b) => b.value - a.value);
    
    // Calculate dynamic height based on number of bars if not specified
    const dynamicHeight = sortedData.length * (barHeight + barPadding) + margin.top + margin.bottom;
    const actualHeight = height || dynamicHeight;
    
    // Select the SVG element
    const svg = d3.select(svgRef.current);
    
    // Clear existing content
    svg.selectAll('*').remove();
    
    // Set SVG dimensions
    svg
      .attr('width', width)
      .attr('height', actualHeight)
      .style('background-color', backgroundColor);
    
    // Create scales
    const xScale = d3.scaleLinear()
      .domain([0, d3.max(sortedData, d => d.value)])
      .range([0, width - margin.left - margin.right]);
    
    const yScale = d3.scaleBand()
      .domain(sortedData.map(d => d.country))
      .range([margin.top, actualHeight - margin.bottom])
      .padding(0.1);
    
    // Create a group for the chart content
    const g = svg.append('g')
      .attr('transform', `translate(${margin.left}, 0)`);
    
    // Add y-axis labels (countries)
    g.selectAll('.country-label')
      .data(sortedData)
      .enter()
      .append('text')
      .attr('class', 'country-label')
      .attr('x', -10)
      .attr('y', d => yScale(d.country) + yScale.bandwidth() / 2)
      .attr('text-anchor', 'end')
      .attr('dominant-baseline', 'middle')
      .attr('fill', textColor)
      .style('font-size', '16px')
      .style('font-weight', d => d.country === highlightCountry ? 'bold' : 'normal')
      .text(d => {
        // Format the value as "10K", "20K", etc.
        const valueInK = d.value / 1000;
        return `${valueInK}${formatSuffix}`;
      });
    
    // Add bars
    const bars = g.selectAll('.bar')
      .data(sortedData)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', 0)
      .attr('y', d => yScale(d.country))
      .attr('width', 0) // Start with width 0 for animation
      .attr('height', yScale.bandwidth())
      .attr('rx', 4) // Rounded corners
      .attr('ry', 4)
      .attr('fill', d => d.country === highlightCountry ? highlightColor : barColor);
    
    // Animate bars
    bars.transition()
      .duration(duration)
      .attr('width', d => xScale(d.value));
    
    // Add country names inside bars
    g.selectAll('.country-name')
      .data(sortedData)
      .enter()
      .append('text')
      .attr('class', 'country-name')
      .attr('x', 10) // Padding from the left edge of the bar
      .attr('y', d => yScale(d.country) + yScale.bandwidth() / 2)
      .attr('dominant-baseline', 'middle')
      .attr('fill', d => d.country === highlightCountry ? '#ffffff' : valueTextColor)
      .style('font-size', '14px')
      .style('font-weight', 'normal')
      .style('opacity', 0) // Start with opacity 0 for animation
      .text(d => d.country)
      .transition()
      .duration(duration)
      .style('opacity', 1);
    
    // Add title
    svg.append('text')
      .attr('class', 'chart-title')
      .attr('x', width / 2)
      .attr('y', margin.top / 2)
      .attr('text-anchor', 'middle')
      .attr('fill', '#ffffff')
      .style('font-size', '24px')
      .style('font-weight', 'bold')
      .text('Maiores mercados consumidores de Cripto');
    
    // Add footer text
    svg.append('text')
      .attr('class', 'chart-footer')
      .attr('x', margin.left)
      .attr('y', actualHeight - 10)
      .attr('fill', '#333333')
      .style('font-size', '14px');
    
    // Add page number
    svg.append('text')
      .attr('class', 'page-number')
      .attr('x', width - margin.right)
      .attr('y', actualHeight - 10)
      .attr('text-anchor', 'end')
      .attr('fill', '#333333')
      .style('font-size', '14px')
      .text('2');
    
    // Make the chart responsive
    const resize = () => {
      const containerWidth = svgRef.current.parentElement.clientWidth;
      if (containerWidth > 0) {
        const newWidth = containerWidth;
        svg.attr('width', newWidth);
        
        // Update scales
        xScale.range([0, newWidth - margin.left - margin.right]);
        
        // Update bars
        g.selectAll('.bar')
          .attr('width', d => xScale(d.value));
        
        // Update title position
        svg.select('.chart-title')
          .attr('x', newWidth / 2);
        
        // Update page number position
        svg.select('.page-number')
          .attr('x', newWidth - margin.right);
      }
    };
    
    // Initial resize
    resize();
    
    // Add resize event listener
    window.addEventListener('resize', resize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', resize);
    };
  }, [data, options, width, height, margin, barHeight, barPadding, highlightCountry, duration, formatSuffix, backgroundColor, barColor, highlightColor, textColor, valueTextColor]);
  
  return svgRef;
};

export default useD3BarChart;
